import React, { useState, useEffect, useRef } from 'react';
import {
  getTechnicalBid,
  updateTechnicalBid,
  uploadTechnicalBidPdf,
  uploadTechnicalBidDocx,
  approveTechnicalBid,
  rejectTechnicalBid,
  getTechnicalBidAudit,
  getTechnicalBidDocumentSignedUrl,
} from '../services/api';
import { formatDate } from '../utils/formatDate';
import { useModalScrollLock } from '../hooks/useModalScrollLock';
import { useFocusTrap } from '../hooks/useFocusTrap';
import { useToast } from '../context/ToastContext';
import './TechnicalBidDetail.css';
import './TenderList.css';

// eslint-disable-next-line no-unused-vars
const STATUS_CLASS = {
  assigned: 'status-participating',
  in_progress: 'status-participating',
  submitted: 'status-submitted',
  approved: 'status-won',
  rejected: 'status-lost',
};

function TechnicalBidDetail({ technicalBidId, user, canManage, onClose }) {
  const { toast } = useToast();
  const mainModalRef = useRef(null);
  const rejectModalRef = useRef(null);
  const [tb, setTb] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [pdfFile, setPdfFile] = useState(null);
  const [docxFile, setDocxFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [rejectFeedback, setRejectFeedback] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [statusDropdownValue, setStatusDropdownValue] = useState('');
  const [documentLoading, setDocumentLoading] = useState(null);

  const isMainOpen = !!technicalBidId && !loading && !error;
  const isRejectOpen = showRejectModal;
  useModalScrollLock(isMainOpen || isRejectOpen);
  useFocusTrap(
    isRejectOpen ? rejectModalRef : mainModalRef,
    isMainOpen || isRejectOpen,
    isRejectOpen ? () => setShowRejectModal(false) : onClose
  );

  const isAssignedToMe = user?.id && tb?.assigned_to_id === user.id;
  const canProductManagerAct = isAssignedToMe && ['assigned', 'in_progress'].includes(tb?.status);
  // eslint-disable-next-line no-unused-vars
  const canTenderManagerAct = canManage && tb?.status === 'submitted';

  useEffect(() => {
    if (!technicalBidId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getTechnicalBid(technicalBidId),
      getTechnicalBidAudit(technicalBidId),
    ])
      .then(([res1, res2]) => {
        if (cancelled) return;
        if (res1.success) setTb(res1.technical_bid);
        if (res2.success) setAudit(res2.audits || []);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [technicalBidId]);

  const refresh = () => {
    getTechnicalBid(technicalBidId).then((res) => {
      if (res.success) setTb(res.technical_bid);
    });
    getTechnicalBidAudit(technicalBidId).then((res) => {
      if (res.success) setAudit(res.audits || []);
    });
  };

  const handleStart = async () => {
    if (!tb || tb.status !== 'assigned') return;
    try {
      setActionLoading(true);
      const res = await updateTechnicalBid(tb.id, { status: 'in_progress' });
      if (res.success) setTb(res.technical_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!tb) return;
    if (!tb.has_pdf) {
      setError('PDF must be uploaded before submitting');
      return;
    }
    try {
      setActionLoading(true);
      const res = await updateTechnicalBid(tb.id, { status: 'submitted' });
      if (res.success) setTb(res.technical_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!tb) return;
    try {
      setActionLoading(true);
      setStatusDropdownValue('');
      const res = await approveTechnicalBid(tb.id);
      if (res.success) setTb(res.technical_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusDropdownChange = async (e) => {
    const value = e.target.value;
    if (!value || value === tb?.status) return;
    setStatusDropdownValue('');
    if (value === 'approved') {
      handleApprove();
      return;
    }
    if (value === 'rejected') {
      setShowRejectModal(true);
      return;
    }
    // assigned | in_progress | submitted – update via API (allows reopen)
    try {
      setActionLoading(true);
      const res = await updateTechnicalBid(tb.id, { status: value });
      if (res.success) setTb(res.technical_bid);
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!tb) return;
    try {
      setActionLoading(true);
      const res = await rejectTechnicalBid(tb.id, { feedback: rejectFeedback || undefined });
      if (res.success) {
        setTb(res.technical_bid);
        setShowRejectModal(false);
        setRejectFeedback('');
      }
      refresh();
    } catch (err) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadPdf = async () => {
    if (!pdfFile || !tb) return;
    setUploadError(null);
    try {
      setActionLoading(true);
      const res = await uploadTechnicalBidPdf(tb.id, pdfFile);
      if (res.success) {
        setTb(res.technical_bid);
        setPdfFile(null);
      }
      refresh();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleUploadDocx = async () => {
    if (!docxFile || !tb) return;
    setUploadError(null);
    try {
      setActionLoading(true);
      const res = await uploadTechnicalBidDocx(tb.id, docxFile);
      if (res.success) {
        setTb(res.technical_bid);
        setDocxFile(null);
      }
      refresh();
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="tb-detail-overlay" onClick={onClose}>
        <div ref={mainModalRef} className="tb-detail-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tb-detail-close" onClick={onClose}>×</button>
          <div className="loading-state"><div className="spinner"></div><p>Loading...</p></div>
        </div>
      </div>
    );
  }

  if (error && !tb) {
    return (
      <div className="tb-detail-overlay" onClick={onClose}>
        <div ref={mainModalRef} className="tb-detail-modal" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="tb-detail-close" onClick={onClose}>×</button>
          <div className="error-message">{error}</div>
          <button type="button" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  if (!tb) return null;

  return (
    <div className="tb-detail-overlay" onClick={onClose} role="presentation">
      <div
        ref={mainModalRef}
        role="dialog"
        aria-modal="true"
        aria-label="Technical Bid detail"
        className="tb-detail-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tb-detail-header">
          <div className="tb-detail-tabs">
            <span className={`tb-detail-tab active`}>{tb.status_display || tb.status}</span>
            <span className="tb-detail-tab">Technical Bid</span>
          </div>
          <button type="button" className="tb-detail-close" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <div className="tb-detail-body">
          {error && (
            <div className="error-message">
              {error}
              <button type="button" onClick={() => setError(null)}>×</button>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <h2 className="tb-detail-id" style={{ margin: 0 }}>{tb.tender_ref || tb.tender_id}</h2>
            <button
              type="button"
              className="edit-btn"
              onClick={() => {
                const ref = tb.tender_ref || tb.tender_id || '';
                if (ref && navigator.clipboard?.writeText) {
                  navigator.clipboard.writeText(ref);
                  toast.success('Reference copied');
                }
              }}
              title="Copy reference"
              aria-label="Copy reference"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="16" height="16">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
              </svg>
            </button>
            {tb.portal_url && (
              <a
                href={tb.portal_url}
                target="_blank"
                rel="noopener noreferrer"
                className="link-btn"
                style={{ fontSize: '13px' }}
              >
                Open in new tab
              </a>
            )}
          </div>
          {tb.tender_title && <p className="tb-detail-title">{tb.tender_title}</p>}

          <div className="tb-detail-grid">
            <div className="tb-detail-item">
              <label>Buyer</label>
              <span>{tb.buyer_name || '-'}</span>
            </div>
            <div className="tb-detail-item">
              <label>Assigned To</label>
              <span>{tb.assigned_to_name || '-'}</span>
            </div>
            <div className="tb-detail-item">
              <label>Due Date</label>
              <span>{formatDate(tb.due_date)}</span>
            </div>
            <div className="tb-detail-item">
              <label>Submission Deadline</label>
              <span>{formatDate(tb.submission_deadline)}</span>
            </div>
            <div className="tb-detail-item">
              <label>Assigned By</label>
              <span>{tb.assigned_by_name || '-'}</span>
            </div>
          </div>
        </div>

        {/* Status Actions */}
        <div className="tb-detail-actions">
          {canProductManagerAct && tb.status === 'assigned' && (
            <button className="confirm-go-ahead-btn" onClick={handleStart} disabled={actionLoading}>
              {actionLoading ? '...' : 'Start'}
            </button>
          )}
          {canProductManagerAct && tb.status === 'in_progress' && (
            <button
              className="confirm-go-ahead-btn"
              onClick={handleSubmit}
              disabled={actionLoading || !tb.has_pdf}
              title={!tb.has_pdf ? 'Upload PDF first' : ''}
            >
              {actionLoading ? '...' : 'Submit'}
            </button>
          )}
          {canManage && (
            <div className="tb-detail-status-dropdown-wrap">
              <label htmlFor="tb-status-dropdown" className="tb-detail-status-dropdown-label">Status</label>
              <select
                id="tb-status-dropdown"
                className="tb-detail-status-dropdown"
                value={tb?.status || ''}
                onChange={handleStatusDropdownChange}
                disabled={actionLoading}
                aria-label="Technical bid status"
              >
                <option value="assigned">Assigned</option>
                <option value="in_progress">In Progress</option>
                <option value="submitted">Submitted</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="tb-detail-section">
          <div className="tb-detail-nav">
            {['info', 'files', 'audit'].map((tab) => (
              <button
                key={tab}
                type="button"
                className={`tb-detail-nav-btn ${activeTab === tab ? 'active' : ''}`}
                onClick={() => setActiveTab(tab)}
              >
                {tab === 'info' ? 'Task Info' : tab === 'files' ? 'Files' : 'Audit Trail'}
              </button>
            ))}
          </div>

          {activeTab === 'info' && (
            <div>
              <label className="tb-detail-instructions-label">Instructions</label>
              <p className="tb-detail-instructions-content">{tb.instructions || '–'}</p>
              {tb.rejection_feedback && (
                <div className="tb-detail-item" style={{ marginTop: '16px' }}>
                  <label>Rejection Feedback</label>
                  <p style={{ margin: '4px 0 0 0', color: '#b91c1c', whiteSpace: 'pre-wrap', fontWeight: 500 }}>{tb.rejection_feedback}</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'files' && (
            <div>
              {uploadError && (
                <div className="error-message" style={{ marginBottom: '12px' }}>
                  {uploadError}
                  <button type="button" onClick={() => setUploadError(null)}>×</button>
                </div>
              )}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>PDF (required)</label>
                {tb.has_pdf ? (
                  <p>
                    <button
                      type="button"
                      className="link-btn"
                      disabled={documentLoading === 'pdf'}
                      onClick={async () => {
                        setDocumentLoading('pdf');
                        try {
                          const url = await getTechnicalBidDocumentSignedUrl(tb.id, 'pdf');
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          setError(e.message || 'Failed to open PDF');
                        } finally {
                          setDocumentLoading(null);
                        }
                      }}
                    >
                      {documentLoading === 'pdf' ? 'Opening...' : 'Download PDF'}
                    </button>
                    {canProductManagerAct && (
                      <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>or replace:</span>
                    )}
                  </p>
                ) : null}
                {canProductManagerAct && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="file"
                      accept=".pdf,application/pdf"
                      onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
                    />
                    <button
                      className="edit-btn"
                      onClick={handleUploadPdf}
                      disabled={!pdfFile || actionLoading}
                    >
                      {actionLoading ? 'Uploading...' : 'Upload PDF'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '6px', fontWeight: 500 }}>DOCX (optional)</label>
                {tb.has_docx ? (
                  <p>
                    <button
                      type="button"
                      className="link-btn"
                      disabled={documentLoading === 'docx'}
                      onClick={async () => {
                        setDocumentLoading('docx');
                        try {
                          const url = await getTechnicalBidDocumentSignedUrl(tb.id, 'docx');
                          window.open(url, '_blank', 'noopener,noreferrer');
                        } catch (e) {
                          setError(e.message || 'Failed to open DOCX');
                        } finally {
                          setDocumentLoading(null);
                        }
                      }}
                    >
                      {documentLoading === 'docx' ? 'Opening...' : 'Download DOCX'}
                    </button>
                    {canProductManagerAct && (
                      <span style={{ marginLeft: '12px', fontSize: '13px', color: '#6b7280' }}>or replace:</span>
                    )}
                  </p>
                ) : null}
                {canProductManagerAct && (
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginTop: '8px' }}>
                    <input
                      type="file"
                      accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      onChange={(e) => setDocxFile(e.target.files?.[0] || null)}
                    />
                    <button
                      className="edit-btn"
                      onClick={handleUploadDocx}
                      disabled={!docxFile || actionLoading}
                    >
                      {actionLoading ? 'Uploading...' : 'Upload DOCX'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'audit' && (
            <div>
              {audit.length === 0 ? (
                <p style={{ color: '#6b7280' }}>No audit entries yet.</p>
              ) : (
                <ul className="tb-detail-audit-list">
                  {audit.map((a, i) => (
                    <li key={a.id || i} className="tb-detail-audit-item">
                      <strong>{a.action}</strong>
                      {a.performed_by_name && <span> by {a.performed_by_name}</span>}
                      <span className="tb-detail-audit-meta">{formatDate(a.created_at)}</span>
                      {(a.previous_value || a.new_value || a.details) && (
                        <div className="tb-detail-audit-details">
                          {a.previous_value && a.new_value && (
                            <span>{a.previous_value} → {a.new_value}</span>
                          )}
                          {a.details && <span>{a.details}</span>}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay" onClick={() => !actionLoading && setShowRejectModal(false)} role="presentation">
          <div
            ref={rejectModalRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="reject-tb-modal-title"
            className="confirm-modal modal--sm"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="reject-tb-modal-title">Reject Technical Bid</h3>
            <p>Provide optional feedback for the product manager:</p>
            <textarea
              value={rejectFeedback}
              onChange={(e) => setRejectFeedback(e.target.value)}
              placeholder="Feedback (optional)"
              rows={3}
              style={{ width: '100%', margin: '12px 0', padding: '10px', borderRadius: '8px', border: '1px solid #d1d5db' }}
            />
            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowRejectModal(false)} disabled={actionLoading}>Cancel</button>
              <button className="cancel-btn" style={{ background: '#b91c1c', color: 'white' }} onClick={handleReject} disabled={actionLoading}>
                {actionLoading ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TechnicalBidDetail;
